/**
 * Design Preview — só para avaliação. Não altera nada do sistema real.
 * Acesse via /preview/moderno
 *
 * Estilo: moderno, fluído, soft — bordas arredondadas, gradientes sutis,
 * sombras difusas, tipografia humanista, micro-animações suaves.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Fuel, Gauge, Trophy, Droplets, Gift, ArrowRight, ChevronLeft, Sparkles } from "lucide-react";

export default function PreviewModerno() {
  const [step, setStep] = useState("home");

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#F7F6F1_0%,#EEECE3_100%)] text-neutral-900 font-sans" data-testid="preview-moderno">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif&family=Geist:wght@300;400;500;600;700&display=swap');
        .font-serif-preview { font-family: 'Instrument Serif', serif; letter-spacing: -0.02em; }
        .font-sans-preview { font-family: 'Geist', system-ui, sans-serif; }
        .glass {
          background: rgba(255,255,255,0.62);
          backdrop-filter: blur(24px) saturate(140%);
          -webkit-backdrop-filter: blur(24px) saturate(140%);
          border: 1px solid rgba(255,255,255,0.8);
          box-shadow: 0 24px 60px -24px rgba(15,23,42,0.18), 0 4px 12px rgba(15,23,42,0.06);
        }
        .card-soft {
          background: rgba(255,255,255,0.9);
          border: 1px solid rgba(15,23,42,0.06);
          box-shadow: 0 10px 30px -12px rgba(15,23,42,0.10);
          transition: transform 260ms cubic-bezier(.2,.9,.3,1), box-shadow 260ms;
        }
        .card-soft:hover { transform: translateY(-3px); box-shadow: 0 20px 40px -16px rgba(15,23,42,0.18); }
        .btn-flow {
          background: linear-gradient(135deg,#111827 0%,#1f2937 100%);
          color: #fff; border-radius: 999px;
          box-shadow: 0 10px 30px -10px rgba(17,24,39,0.5);
          transition: transform 220ms, box-shadow 220ms;
        }
        .btn-flow:hover { transform: translateY(-2px); box-shadow: 0 14px 34px -10px rgba(17,24,39,0.55); }
        .btn-accent {
          background: linear-gradient(135deg,#FFB347 0%,#FF7E5F 100%);
          color: #1b1b1b; border-radius: 999px;
          box-shadow: 0 10px 28px -10px rgba(255,126,95,0.6);
        }
        .progress-track { background: rgba(15,23,42,0.06); border-radius: 999px; height: 10px; overflow: hidden; }
        .progress-fill { height: 100%; background: linear-gradient(90deg,#8B5CF6 0%,#EC4899 100%); border-radius: 999px; transition: width 800ms cubic-bezier(.2,.9,.3,1); }
        .progress-fill.gold { background: linear-gradient(90deg,#F59E0B 0%,#FDE68A 100%); }
        .chip {
          display: inline-flex; align-items:center; gap:6px;
          padding: 6px 12px; border-radius: 999px;
          background: rgba(139,92,246,0.08); color:#6D28D9;
          font-weight: 500; font-size: 12px;
        }
        .digit-input {
          border: none; background: rgba(255,255,255,0.7);
          border-radius: 28px; padding: 28px 32px;
          box-shadow: inset 0 2px 8px rgba(15,23,42,0.08);
          font-family: 'Geist', sans-serif; font-size: 44px; letter-spacing: 4px;
          text-align: center; width: 100%;
        }
        .digit-input:focus { outline: 2px solid #8B5CF6; outline-offset: 2px; }
        @keyframes floatIn { from { opacity:0; transform: translateY(12px); } to { opacity:1; transform: translateY(0); } }
        .float-in { animation: floatIn 500ms cubic-bezier(.2,.9,.3,1); }
        .pulse-dot { width:8px; height:8px; border-radius:50%; background:#22C55E; box-shadow:0 0 0 0 rgba(34,197,94,0.6); animation: pulseDot 1.6s infinite; }
        @keyframes pulseDot { 70% { box-shadow:0 0 0 12px rgba(34,197,94,0); } 100% { box-shadow:0 0 0 0 rgba(34,197,94,0); } }
      `}</style>

      {/* Top bar */}
      <header className="px-6 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl glass flex items-center justify-center">
            <Fuel size={18} className="text-[#7C3AED]"/>
          </div>
          <div>
            <div className="font-sans-preview text-sm font-semibold">Posto Santa Clara</div>
            <div className="text-xs text-neutral-500 flex items-center gap-2">
              <span className="pulse-dot"/> Caixa aberto · Turno de João
            </div>
          </div>
        </div>
        <Link to="/" className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors">← Voltar ao sistema real</Link>
      </header>

      {/* Preview nav */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="glass rounded-full p-1 flex gap-1 w-fit">
          {[
            ["home", "Telefone"],
            ["identify", "Cliente"],
            ["product", "Combustível"],
            ["confirm", "Confirmação"],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setStep(k)} data-testid={`preview-nav-${k}`}
                    className={`px-4 h-9 rounded-full text-xs font-medium transition-colors ${step === k ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-white/60"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {step === "home" && <HomeStep onNext={() => setStep("identify")}/>}
        {step === "identify" && <IdentifyStep onNext={() => setStep("product")} onBack={() => setStep("home")}/>}
        {step === "product" && <ProductStep onNext={() => setStep("confirm")} onBack={() => setStep("identify")}/>}
        {step === "confirm" && <ConfirmStep onRestart={() => setStep("home")} onBack={() => setStep("product")}/>}
      </main>

      <footer className="max-w-6xl mx-auto px-6 py-6 text-xs text-neutral-500 flex items-center gap-2">
        <Sparkles size={14}/> <span>Preview de design. Nenhuma alteração aplicada ao sistema real.</span>
      </footer>
    </div>
  );
}

function HomeStep({ onNext }) {
  const [phone, setPhone] = useState("");
  return (
    <div className="float-in">
      <div className="chip mb-4"><Sparkles size={12}/> Atendimento · Início</div>
      <h1 className="font-serif-preview text-6xl md:text-7xl leading-none mb-4">
        Qual o telefone<br/>do cliente?
      </h1>
      <p className="text-neutral-500 mb-8 max-w-lg">
        Digite o WhatsApp para identificar cadastro. Cliente novo? Faça o primeiro acesso abaixo.
      </p>

      <div className="glass rounded-3xl p-6">
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(11) 98765-4321"
               inputMode="numeric" className="digit-input" autoFocus/>
        <div className="grid grid-cols-2 gap-3 mt-5">
          <button className="h-16 rounded-2xl bg-white/70 border border-neutral-200 hover:bg-white font-medium transition-all hover:shadow-lg">
            Primeiro Acesso
          </button>
          <button onClick={onNext} className="h-16 btn-flow font-medium flex items-center justify-center gap-2">
            Buscar Cliente <ArrowRight size={16}/>
          </button>
        </div>
      </div>
    </div>
  );
}

function IdentifyStep({ onNext, onBack }) {
  return (
    <div className="float-in">
      <button onClick={onBack} className="text-sm text-neutral-500 hover:text-neutral-900 mb-4 flex items-center gap-1"><ChevronLeft size={14}/> voltar</button>
      <div className="chip mb-4">Cliente identificado</div>
      <h1 className="font-serif-preview text-6xl md:text-7xl leading-none mb-2">Ana Luiza Ferreira</h1>
      <div className="text-neutral-500 mb-10">(11) 99887-7655 · ABC1D23 · 24 abastecimentos</div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <MiniCard label="Último abastecimento" value="há 12 dias" subtle/>
        <MiniCard label="Gasto médio" value="R$ 142" subtle/>
        <MiniCard label="Combustível favorito" value="Aditivada" subtle/>
      </div>

      <div className="glass rounded-3xl p-6">
        <div className="text-sm font-medium mb-3">Enviar código de verificação</div>
        <div className="grid grid-cols-2 gap-3">
          <button className="h-16 rounded-2xl bg-white/70 border border-neutral-200 hover:bg-white font-medium transition-all hover:shadow-lg">
            Acesso sem celular
          </button>
          <button onClick={onNext} className="h-16 btn-flow font-medium flex items-center justify-center gap-2">
            Enviar código <ArrowRight size={16}/>
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductStep({ onNext, onBack }) {
  const produtos = [
    { id: "gc", nome: "Gasolina Comum", bomba: "R$ 5,89", cad: "R$ 5,79", cor: "from-purple-500 to-pink-500" },
    { id: "ga", nome: "Gasolina Aditivada", bomba: "R$ 5,99", cad: "R$ 5,89", cor: "from-amber-400 to-orange-500" },
    { id: "et", nome: "Etanol", bomba: "R$ 3,99", cad: "R$ 3,89", cor: "from-emerald-400 to-teal-500" },
    { id: "d10", nome: "Diesel S10", bomba: "R$ 6,29", cad: "R$ 6,19", cor: "from-sky-500 to-indigo-500" },
  ];
  return (
    <div className="float-in">
      <button onClick={onBack} className="text-sm text-neutral-500 hover:text-neutral-900 mb-4 flex items-center gap-1"><ChevronLeft size={14}/> voltar</button>
      <h1 className="font-serif-preview text-5xl md:text-6xl leading-none mb-2">Ana está pronta.</h1>
      <p className="text-neutral-500 mb-8">Escolha o combustível e veja os descontos ativos automaticamente.</p>

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <ProgressCard icon={Trophy} title="Prêmio Fidelidade" atingido={134.6} meta={200} colorClass="" suffix="L"
                      hint="Faltam 65,4 L para 10¢ de desconto por litro"/>
        <ProgressCard icon={Droplets} title="Troca de Óleo" atingido={312.0} meta={500} colorClass="gold" suffix="L"
                      hint="Faltam 188 L para 20% off na troca"/>
      </div>

      <div className="glass rounded-3xl p-4 mb-8">
        <div className="flex items-center gap-2 text-sm mb-3">
          <Gift size={14} className="text-[#EC4899]"/> <span className="font-medium">3 descontos disponíveis</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="chip">Cliente frequente · 5¢/L</span>
          <span className="chip">Promoção do dia · 5¢/L</span>
          <span className="chip" style={{ background:"rgba(236,72,153,0.10)", color:"#BE185D" }}>Cashback · R$ 8,40</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {produtos.map((p, i) => (
          <button key={p.id} onClick={onNext} data-testid={`preview-prod-${p.id}`}
                  className="card-soft rounded-3xl p-5 text-left group" style={{ animationDelay: `${i * 80}ms` }}>
            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${p.cor} flex items-center justify-center mb-4 shadow-md`}>
              <Fuel size={20} className="text-white"/>
            </div>
            <div className="font-serif-preview text-3xl mb-2">{p.nome}</div>
            <div className="flex items-end justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-neutral-400">Cadastro</div>
                <div className="font-sans-preview text-2xl font-semibold text-neutral-900">{p.cad}</div>
                <div className="text-xs text-neutral-400 line-through">Bomba {p.bomba}</div>
              </div>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity text-neutral-400">
                <ArrowRight size={20}/>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ConfirmStep({ onRestart, onBack }) {
  return (
    <div className="float-in">
      <button onClick={onBack} className="text-sm text-neutral-500 hover:text-neutral-900 mb-4 flex items-center gap-1"><ChevronLeft size={14}/> voltar</button>
      <div className="chip mb-6" style={{ background:"rgba(34,197,94,0.10)", color:"#15803D" }}>
        <Gauge size={12}/> Pronto para abastecer
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="relative rounded-[32px] p-8 text-neutral-900"
             style={{ background: "linear-gradient(135deg,#FEF3C7 0%,#FDE68A 50%,#F59E0B 100%)" }}>
          <div className="text-sm uppercase tracking-wider text-neutral-700 mb-2">Digite na bomba</div>
          <div className="font-serif-preview text-[84px] md:text-[104px] leading-[0.85] font-normal">R$ 118,00</div>
          <div className="mt-4 text-sm text-neutral-700">20,00 L × R$ 5,90/L</div>
          <div className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/40 flex items-center justify-center">
            <Gauge size={18}/>
          </div>
        </div>

        <div className="relative rounded-[32px] p-8 text-white"
             style={{ background: "linear-gradient(135deg,#1E1B4B 0%,#4C1D95 50%,#7C3AED 100%)" }}>
          <div className="text-sm uppercase tracking-wider text-white/70 mb-2">Cobre do cliente</div>
          <div className="font-serif-preview text-[84px] md:text-[104px] leading-[0.85] font-normal">R$ 115,80</div>
          <div className="mt-4 text-sm text-white/70">Desconto R$ 2,20 · cliente frequente</div>
          <div className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
            <Sparkles size={18}/>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3">
        <button className="h-16 rounded-full bg-white/80 border border-neutral-200 hover:bg-white font-medium transition-all hover:shadow-lg">
          Cancelar
        </button>
        <button onClick={onRestart} className="h-16 btn-accent font-semibold flex items-center justify-center gap-2">
          Confirmar abastecimento <ArrowRight size={16}/>
        </button>
      </div>
    </div>
  );
}

function MiniCard({ label, value, subtle }) {
  return (
    <div className={`rounded-2xl p-4 ${subtle ? "bg-white/50 border border-white/80" : "glass"}`}>
      <div className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="font-serif-preview text-2xl mt-1">{value}</div>
    </div>
  );
}

function ProgressCard({ icon: Icon, title, atingido, meta, colorClass, suffix, hint }) {
  const pct = Math.min(100, (atingido / meta) * 100);
  return (
    <div className="card-soft rounded-3xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-neutral-900 text-white flex items-center justify-center">
            <Icon size={14}/>
          </div>
          <div className="text-sm font-medium">{title}</div>
        </div>
        <div className="text-xs text-neutral-500">{Math.round(pct)}%</div>
      </div>
      <div className="progress-track mb-2"><div className={`progress-fill ${colorClass}`} style={{ width: `${pct}%` }}/></div>
      <div className="flex items-baseline justify-between">
        <div className="font-serif-preview text-2xl">
          {atingido.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} / {meta} <span className="text-sm text-neutral-500">{suffix}</span>
        </div>
      </div>
      <div className="text-xs text-neutral-500 mt-1">{hint}</div>
    </div>
  );
}
